import { api } from "@/redux/api";

import { ListPerson } from "../../pages/List/schema/list";

/**
 * The dummy-prospect replacement mutations behind the "Select prospect" / "Find leads" flow
 * (SelectProspectCell, NoContactYet, FeedSelectProspect, SelectProspectActionButton). Split into
 * its own `injectEndpoints` file because the main `list.api.ts` is at the file-length ratchet
 * ceiling and may not grow; RTK Query supports injecting endpoints into the same base `api`
 * across files.
 *
 * Both mutations invalidate the broad `ListPerson` tag — on success only, so a failed pick keeps
 * the search modal open.
 */
/**
 * Whether a rejected Select or Replace lost to someone who filled the row first (PREC-4570): a
 * 409, which the API answers with the code ROW_NO_LONGER_PLACEHOLDER (`ERROR_TYPE`). The person
 * shown on the card is then out of date, so the caller refreshes it.
 */
export const isRowTakenError = (error: unknown): boolean =>
	(error as { status?: unknown } | null)?.status === 409;

export const listPersonProspectApi = api.injectEndpoints({
	endpoints: (builder) => ({
		selectProspectForListPerson: builder.mutation<
			ListPerson,
			{ listPersonId: number; linkedinUrl: string; trackerOrganizationId: number }
		>({
			query: ({ listPersonId, ...body }) => ({
				url: `list_person/${listPersonId}/select-prospect`,
				method: "POST",
				body,
			}),
			invalidatesTags: (result, error) => (error ? [] : ["ListPerson"]),
		}),
		replaceProspectForListPerson: builder.mutation<
			ListPerson,
			{ listPersonId: number; linkedinUrl: string }
		>({
			query: ({ listPersonId, linkedinUrl }) => ({
				url: `list_person/${listPersonId}/replace-prospect`,
				method: "POST",
				body: { linkedinUrl },
			}),
			invalidatesTags: (result, error) => (error ? [] : ["ListPerson"]),
		}),
	}),
});

export const { useSelectProspectForListPersonMutation, useReplaceProspectForListPersonMutation } =
	listPersonProspectApi;
